import React, { useEffect, useRef, useState } from 'react';
import { Moon, Sun, Check, Pipette } from 'lucide-react';
import { useTheme, CUSTOM_SIDEBAR_THEME, CUSTOM_PANEL_THEME, DEFAULT_SIDEBAR_THEME } from '../../context/ThemeContext';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { saveUiPreferences, mergeUiPreferences } from '../../utils/api';
import { resetGridDensity } from '../../modules/forecast-v3/hooks/useGridDensity';
import { queueNewLookTour, startNewLookTour } from '../NewLookTour/NewLookTour';
import { CUSTOM_CHART_PALETTE, DEFAULT_CHART_PALETTE } from '../../styles/chartColors';
import {
  isLightColor, normalizeHex, sidebarPreviewFor, deriveChartPalette, derivePanelVars,
} from '../../utils/sidebarPalette';

/**
 * Settings → Appearance. Five choices, each saved to the user's account
 * (uiPreferences on the Cosmos user doc, applied on every device):
 *   Layout · Mode (light / dark) · Sidebar · Main panel · Data colours.
 * Sidebar, panel and data colours offer presets plus a custom picker and
 * only apply to the New layout; Classic keeps its fixed look.
 */

/* ------------------------------------------------------------------ */
/* previews                                                            */
/* ------------------------------------------------------------------ */

/** Mini chart for the data-colour tiles: lead line, support line, benchmark dashes. */
function ChartPalettePreview({ colors, dark }) {
  const lead = 'M4,40 C14,38 20,26 30,28 S46,20 56,22 S72,12 84,14 S100,8 112,6';
  const support = 'M4,46 C16,44 24,38 34,40 S50,34 60,36 S76,30 88,30 S104,26 112,24';
  const bench = 'M4,34 C20,32 30,36 44,34 S66,30 80,32 S100,28 112,30';
  const bg = dark ? '#121216' : '#FFFFFF';
  const grid = dark ? '#2A2A33' : '#E8E8F0';
  return (
    <div aria-hidden="true" style={{ height: 64, borderRadius: 8, background: bg, border: `1px solid ${grid}`, padding: '6px 4px 2px', overflow: 'hidden' }}>
      <svg viewBox="0 0 116 52" width="100%" height="100%" fill="none" preserveAspectRatio="none">
        {[10, 22, 34, 46].map((y) => <line key={y} x1="4" x2="112" y1={y} y2={y} stroke={grid} strokeWidth="1" />)}
        <path d={bench} stroke={colors.tertiary} strokeWidth="1.5" strokeDasharray="3 3" />
        <path d={support} stroke={colors.secondary} strokeWidth="2" />
        <path d={lead} stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="112" cy="6" r="3" fill={bg} stroke={colors.primary} strokeWidth="2" />
      </svg>
    </div>
  );
}

/** Mini app for the Layout tiles: classic (light canvas, rail, tray) vs inset (rail, card, header). */
function ShellPreview({ kind }) {
  const inset = kind === 'inset';
  return (
    <div className="flex h-[88px] gap-1.5 overflow-hidden rounded-lg p-1.5" style={{ background: '#F1F1F5', boxShadow: 'inset 0 0 0 1px #E2E2E9' }} aria-hidden="true">
      <div className="flex w-[30%] flex-col gap-1.5 rounded-md p-1.5" style={{ background: inset ? 'transparent' : '#FFFFFF', boxShadow: inset ? undefined : '0 1px 2px rgba(15,15,17,0.06)' }}>
        <div className="h-1.5 w-3/4 rounded-full" style={{ background: '#3B3B45' }} />
        <div className="flex items-center gap-1 rounded px-1 py-1" style={{ background: inset ? '#E4E4EB' : '#FFF4ED' }}>
          <span className="h-2.5 w-0.5 rounded-full" style={{ background: '#FF7A00' }} />
          <span className="h-1.5 w-2/3 rounded-full" style={{ background: inset ? '#3B3B45' : '#C2410C' }} />
        </div>
        <div className="mx-1 h-1.5 w-1/2 rounded-full" style={{ background: '#CFCFD8' }} />
        <div className="mx-1 h-1.5 w-2/3 rounded-full" style={{ background: '#CFCFD8' }} />
      </div>
      <div className="relative flex flex-1 flex-col gap-1.5 rounded-md p-1.5" style={{ background: inset ? '#FFFFFF' : 'transparent', boxShadow: inset ? '0 1px 2px rgba(15,15,17,0.06)' : undefined }}>
        {inset ? (
          <div className="flex h-3 items-center gap-1 border-b pb-1" style={{ borderColor: '#ECECF1' }}>
            <span className="h-1.5 w-1/3 rounded-full" style={{ background: '#D9D9E0' }} />
            <span className="ml-auto h-2 w-2 rounded-full" style={{ background: '#ECECF1' }} />
            <span className="h-2 w-2 rounded-full" style={{ background: '#ECECF1' }} />
          </div>
        ) : (
          <div className="absolute right-1.5 top-1.5 flex h-3 items-center gap-1 rounded-full bg-white px-1" style={{ boxShadow: '0 1px 3px rgba(15,15,17,0.12)' }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#D9D9E0' }} />
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#D9D9E0' }} />
          </div>
        )}
        <div className="h-1.5 w-1/2 rounded-full" style={{ background: '#D9D9E0', marginTop: inset ? 0 : 14 }} />
        <div className="h-1.5 w-5/6 rounded-full" style={{ background: '#ECECF1' }} />
        <div className="mt-auto h-5 rounded" style={{ background: inset ? '#F4F4F7' : '#FFFFFF', boxShadow: inset ? undefined : '0 1px 2px rgba(15,15,17,0.06)' }} />
      </div>
    </div>
  );
}

/** Mini shell (rail + content card). `panel` colours the card; `rail` the sidebar. */
function ShellSwatch({ rail, panel, outlined }) {
  const { bg, line, active: activeRow, text } = rail;
  const panelBorder = panel.border || 'transparent';
  const panelLight = isLightColor(panel.bg);
  return (
    <div className="flex h-[88px] gap-1.5 overflow-hidden rounded-lg p-1.5" style={{ background: bg, boxShadow: outlined ? 'inset 0 0 0 1px #E2E2E9' : undefined }} aria-hidden="true">
      <div className="flex w-[38%] flex-col gap-1.5 pt-1">
        <div className="h-1.5 w-3/4 rounded-full" style={{ background: text, opacity: 0.85 }} />
        <div className="mt-1 flex items-center gap-1 rounded px-1 py-1" style={{ background: activeRow }}>
          <span className="h-2.5 w-0.5 rounded-full" style={{ background: '#FF7A00' }} />
          <span className="h-1.5 w-2/3 rounded-full" style={{ background: text }} />
        </div>
        <div className="mx-1 h-1.5 w-1/2 rounded-full" style={{ background: line }} />
        <div className="mx-1 h-1.5 w-2/3 rounded-full" style={{ background: line }} />
      </div>
      <div className="flex flex-1 flex-col gap-1.5 rounded-md p-1.5" style={{ background: panel.bg, boxShadow: `inset 0 0 0 1px ${panelBorder}` }}>
        <div className="h-1.5 w-1/2 rounded-full" style={{ background: panelLight ? '#C8C8D4' : '#4A4A58' }} />
        <div className="h-1.5 w-5/6 rounded-full" style={{ background: panelLight ? '#E4E4EC' : '#2E2E38' }} />
        <div className="mt-auto h-5 rounded" style={{ background: panelLight ? 'rgba(15,15,17,0.04)' : 'rgba(255,255,255,0.05)' }} />
      </div>
    </div>
  );
}

/** Light / dark mode tiles: grey canvas + white panel vs onyx. */
function ModePreview({ dark }) {
  return (
    <ShellSwatch
      rail={dark ? { bg: '#0B0B0D', line: '#3A3A44', active: '#1C1C22', text: '#D6D6E0' } : { bg: '#F1F1F5', line: '#CFCFD8', active: '#E4E4EB', text: '#3B3B45' }}
      panel={dark ? { bg: '#121216', border: '#26262D' } : { bg: '#FFFFFF', border: '#E2E2E9' }}
      outlined={!dark}
    />
  );
}

/* ------------------------------------------------------------------ */
/* tiles                                                               */
/* ------------------------------------------------------------------ */

const gridStyle = { gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' };
const groupLabelStyle = { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--lodge-onyx-400)' };
const sectionStyle = {
  padding: 'var(--sp-6)', borderRadius: 'var(--radius-xl)', background: 'var(--lodge-white)',
  border: '1px solid var(--lodge-onyx-100)', boxShadow: 'var(--shadow-sm)',
};
function tileStyle(active) {
  return {
    borderColor: active ? 'var(--lodge-orange-400)' : 'var(--lodge-onyx-100)',
    background: active ? 'var(--lodge-orange-50)' : 'var(--lodge-white)',
  };
}

function Tile({ active, onSelect, name, description, badge, children }) {
  return (
    <button type="button" role="radio" aria-checked={active} onClick={onSelect} className="rounded-xl border-2 p-2 text-left transition-all" style={tileStyle(active)}>
      {children}
      <div className="mt-2 flex items-center gap-2 px-1">
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--lodge-onyx-800)' }}>{name}</span>
        {badge && <span style={{ fontSize: 11, color: 'var(--lodge-onyx-400)' }}>{badge}</span>}
        {active && <Check size={14} style={{ color: 'var(--lodge-orange-500)', marginLeft: 'auto' }} />}
      </div>
      {description && <div className="px-1" style={{ fontSize: 12, color: 'var(--lodge-onyx-500)' }}>{description}</div>}
    </button>
  );
}

/** "Custom" tile: preview + native colour picker + hex field. */
function CustomTile({ active, color, onSelect, onColor, label, children }) {
  const [draft, setDraft] = useState(color);
  useEffect(() => setDraft(color), [color]);
  const draftValid = !!normalizeHex(draft);
  return (
    <div className="rounded-xl border-2 p-2 transition-all" style={tileStyle(active)}>
      <button type="button" role="radio" aria-checked={active} onClick={onSelect} className="block w-full text-left">
        {children}
        <div className="mt-2 flex items-center gap-2 px-1">
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--lodge-onyx-800)' }}>Custom</span>
          {active && <Check size={14} style={{ color: 'var(--lodge-orange-500)', marginLeft: 'auto' }} />}
        </div>
      </button>
      <div className="mt-1 flex items-center gap-2 px-1">
        <label
          className="relative inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md"
          style={{ background: color, boxShadow: 'inset 0 0 0 1px rgba(15,15,17,0.15)' }}
          title="Pick a colour"
        >
          <Pipette size={13} style={{ color: '#fff', mixBlendMode: 'difference' }} aria-hidden="true" />
          <input type="color" value={color} onChange={(e) => onColor(e.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" aria-label={`${label} colour`} />
        </label>
        <input
          type="text"
          value={draft}
          spellCheck={false}
          maxLength={7}
          onChange={(e) => {
            setDraft(e.target.value);
            // apply only complete 6-digit values while typing (#abc stays a draft)
            if (/^#?[0-9a-f]{6}$/i.test(e.target.value.trim())) onColor(e.target.value);
          }}
          onBlur={() => setDraft(color)}
          aria-label={`${label} colour hex`}
          aria-invalid={!draftValid}
          className="h-7 min-w-0 flex-1 rounded-md border px-2 font-mono text-xs uppercase"
          style={{ borderColor: draftValid ? 'var(--lodge-onyx-100)' : 'var(--color-error, #D92D20)', background: 'var(--lodge-white)', color: 'var(--lodge-onyx-800)' }}
        />
      </div>
    </div>
  );
}

function SaveStatus({ state }) {
  if (!state) return null;
  return (
    <span role="status" style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: state === 'error' ? 'var(--color-error, #D92D20)' : 'var(--lodge-onyx-400)' }}>
      {state === 'saving' ? 'Saving…' : state === 'saved' ? 'Saved' : 'Saved on this device only'}
    </span>
  );
}

function Section({ title, description, status, children }) {
  return (
    <section style={sectionStyle}>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>{title}</h3>
          <p style={{ fontSize: 13, color: 'var(--lodge-onyx-500)', marginTop: 2 }}>{description}</p>
        </div>
        <SaveStatus state={status} />
      </div>
      {children}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* page                                                                */
/* ------------------------------------------------------------------ */

const AppearanceSettings = () => {
  const {
    isDarkMode, toggleDarkMode,
    uiShell, setUiShell, uiShells, resetNewLookPreferences,
    sidebarTheme, setSidebarTheme, sidebarThemes, sidebarCustomColor, setSidebarCustomColor,
    panelTheme, panelThemes, panelThemeByMode, setPanelTheme, panelCustomColor, panelCustomColorByMode, setPanelCustomColor,
    chartPalette, setChartPalette, chartPalettes, chartCustomColor, setChartCustomColor,
  } = useTheme();
  const { userSettings, updateSettings } = useUserSettingsContext();
  const [status, setStatus] = useState({}); // section → null | 'saving' | 'saved' | 'error'
  const timers = useRef({});
  const setSectionStatus = (key) => (next) => setStatus((s) => ({ ...s, [key]: typeof next === 'function' ? next(s[key]) : next }));
  const inset = uiShell === 'inset';
  const mode = isDarkMode ? 'dark' : 'light';

  // Persist a partial uiPreferences patch on the Cosmos user doc and mirror it
  // into the settings context (initData hydrates it on other devices).
  const persist = async (key, patch) => {
    if (!userSettings?.email) return;
    const set = setSectionStatus(key);
    set('saving');
    try {
      const uiPreferences = mergeUiPreferences(userSettings.uiPreferences, patch);
      await saveUiPreferences(userSettings.email, patch);
      await updateSettings({ uiPreferences });
      set('saved');
      setTimeout(() => set((s) => (s === 'saved' ? null : s)), 2500);
    } catch (e) {
      console.error('Error saving appearance preference:', e);
      set('error');
    }
  };
  // Colour inputs fire on every drag step: apply live, save after a pause.
  const persistDebounced = (key, patchFn) => {
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => persist(key, patchFn()), 600);
  };
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  // Going back to Classic also clears the New look's personalisation (sidebar,
  // panel and data colours, forecast grid density) locally and on the user
  // doc, so switching to New again starts from the true defaults.
  const NEW_LOOK_PREFERENCE_KEYS = ['sidebarTheme', 'sidebarCustomColor', 'panelTheme', 'panelCustomColor', 'chartPalette', 'chartCustomColor', 'forecastDensity'];
  const chooseShell = (id) => {
    if (id === uiShell) return;
    setUiShell(id);
    if (id === 'classic') {
      resetNewLookPreferences();
      resetGridDensity();
      persist('shell', { shell: id, ...Object.fromEntries(NEW_LOOK_PREFERENCE_KEYS.map((key) => [key, null])) });
      return;
    }
    queueNewLookTour();
    persist('shell', { shell: id });
  };
  const chooseMode = (dark) => { if (dark !== isDarkMode) toggleDarkMode(); };
  const chooseSidebar = (id) => { setSidebarTheme(id); persist('sidebar', { sidebarTheme: id }); };
  const chooseSidebarColor = (hex) => {
    setSidebarCustomColor(hex);
    persistDebounced('sidebar', () => ({ sidebarTheme: CUSTOM_SIDEBAR_THEME, sidebarCustomColor: normalizeHex(hex) }));
  };
  const choosePanel = (id) => {
    setPanelTheme(id);
    persist('panel', { panelTheme: { ...panelThemeByMode, [mode]: id } });
  };
  const choosePanelColor = (hex) => {
    setPanelCustomColor(hex);
    persistDebounced('panel', () => ({
      panelTheme: { ...panelThemeByMode, [mode]: CUSTOM_PANEL_THEME },
      panelCustomColor: { ...panelCustomColorByMode, [mode]: normalizeHex(hex) },
    }));
  };
  const chooseChart = (id) => { if (id !== chartPalette) { setChartPalette(id); persist('chart', { chartPalette: id }); } };
  const chooseChartColor = (hex) => {
    setChartCustomColor(hex);
    persistDebounced('chart', () => ({ chartPalette: CUSTOM_CHART_PALETTE, chartCustomColor: normalizeHex(hex) }));
  };

  // Swatch inputs for the current mode
  const railPreview = sidebarTheme === CUSTOM_SIDEBAR_THEME
    ? sidebarPreviewFor(sidebarCustomColor)
    : (sidebarThemes.find((t) => t.id === sidebarTheme) || sidebarThemes.find((t) => t.id === DEFAULT_SIDEBAR_THEME)).preview;
  const railForMode = sidebarTheme === 'match' && isDarkMode ? sidebarThemes.find((t) => t.id === 'onyx').preview : railPreview;
  const currentPanel = panelTheme === CUSTOM_PANEL_THEME
    ? { bg: panelCustomColor, border: derivePanelVars(panelCustomColor)?.['--shell-card-border'] }
    : { bg: (panelThemes.find((t) => t.id === panelTheme) || {}).bg || (isDarkMode ? '#121216' : '#FFFFFF'), border: isDarkMode ? '#26262D' : '#E2E2E9' };
  const customChartColors = deriveChartPalette(chartCustomColor);

  return (
    <div className="space-y-6">
      <div>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 22, fontWeight: 700, color: 'var(--lodge-onyx-900)' }}>Appearance</h1>
        <p style={{ fontSize: 14, color: 'var(--lodge-onyx-500)', marginTop: 4 }}>How Lodgeic looks for you. Saved to your account, so it follows you to other devices.</p>
      </div>

      <Section title="Layout" description="Choose the desktop layout." status={status.shell}>
        <div className="grid gap-3" style={gridStyle} role="radiogroup" aria-label="Desktop layout">
          {uiShells.map((shell) => (
            <Tile key={shell.id} active={uiShell === shell.id} onSelect={() => chooseShell(shell.id)} name={shell.name} description={shell.description}>
              <ShellPreview kind={shell.id} />
            </Tile>
          ))}
        </div>
        {inset && (
          <button
            type="button"
            onClick={startNewLookTour}
            className="mt-3 text-[13px] font-medium text-[var(--lodge-onyx-500)] underline-offset-2 hover:text-[var(--lodge-onyx-900)] hover:underline"
          >
            Take the tour of the new look
          </button>
        )}
      </Section>

      <Section title="Mode" description="Light is a grey canvas with a white panel; Dark is onyx.">
        <div className="grid gap-3" style={gridStyle} role="radiogroup" aria-label="Light or dark mode">
          <Tile active={!isDarkMode} onSelect={() => chooseMode(false)} name={<span className="inline-flex items-center gap-1.5"><Sun size={13} style={{ color: 'var(--lodge-orange-500)' }} />Light</span>} badge="Default">
            <ModePreview dark={false} />
          </Tile>
          <Tile active={isDarkMode} onSelect={() => chooseMode(true)} name={<span className="inline-flex items-center gap-1.5"><Moon size={13} style={{ color: 'var(--lodge-onyx-500)' }} />Dark</span>}>
            <ModePreview dark />
          </Tile>
        </div>
      </Section>

      {inset && (
        <Section title="Sidebar" description="The navigation rail colour." status={status.sidebar}>
          <div role="radiogroup" aria-label="Sidebar colour" className="space-y-5">
            {[{ tone: 'light', label: 'Light' }, { tone: 'dark', label: 'Dark' }].map((group) => (
              <div key={group.tone}>
                <div className="mb-2" style={groupLabelStyle}>{group.label}</div>
                <div className="grid gap-3" style={gridStyle}>
                  {sidebarThemes.filter((t) => t.tone === group.tone).map((theme) => (
                    <Tile key={theme.id} active={sidebarTheme === theme.id} onSelect={() => chooseSidebar(theme.id)} name={theme.name} description={theme.description} badge={theme.id === DEFAULT_SIDEBAR_THEME ? 'Default' : undefined}>
                      <ShellSwatch rail={theme.preview} panel={currentPanel} outlined={theme.tone === 'light'} />
                    </Tile>
                  ))}
                  {group.tone === 'dark' && (
                    <CustomTile active={sidebarTheme === CUSTOM_SIDEBAR_THEME} color={sidebarCustomColor} onSelect={() => chooseSidebar(CUSTOM_SIDEBAR_THEME)} onColor={chooseSidebarColor} label="Custom sidebar">
                      <ShellSwatch rail={sidebarPreviewFor(sidebarCustomColor)} panel={currentPanel} outlined={isLightColor(sidebarCustomColor)} />
                    </CustomTile>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {inset && (
        <Section title="Main panel" description={`The content panel colour in ${isDarkMode ? 'dark' : 'light'} mode. Each mode keeps its own choice.`} status={status.panel}>
          <div role="radiogroup" aria-label="Main panel colour" className="grid gap-3" style={gridStyle}>
            {panelThemes.filter((t) => t.tone === mode).map((t) => (
              <Tile key={t.id} active={panelTheme === t.id} onSelect={() => choosePanel(t.id)} name={t.name} description={t.description === 'Default' ? undefined : t.description} badge={t.description === 'Default' ? 'Default' : undefined}>
                <ShellSwatch rail={railForMode} panel={{ bg: t.bg, border: isDarkColorSafe(t.bg) ? '#2E2F36' : '#E2E2E9' }} outlined={!isDarkMode} />
              </Tile>
            ))}
            <CustomTile active={panelTheme === CUSTOM_PANEL_THEME} color={panelCustomColor} onSelect={() => choosePanel(CUSTOM_PANEL_THEME)} onColor={choosePanelColor} label="Custom panel">
              <ShellSwatch rail={railForMode} panel={{ bg: panelCustomColor, border: derivePanelVars(panelCustomColor)?.['--shell-card-border'] }} outlined={!isDarkMode} />
            </CustomTile>
          </div>
        </Section>
      )}

      {inset && (
        <Section title="Data colours" description="Series colours for report and dashboard charts." status={status.chart}>
          <div role="radiogroup" aria-label="Data colours" className="grid gap-3" style={gridStyle}>
            {chartPalettes.map((pal) => (
              <Tile key={pal.id} active={chartPalette === pal.id} onSelect={() => chooseChart(pal.id)} name={pal.name} description={pal.description} badge={pal.id === DEFAULT_CHART_PALETTE ? 'Default' : undefined}>
                <ChartPalettePreview colors={isDarkMode ? pal.dark : pal.light} dark={isDarkMode} />
              </Tile>
            ))}
            <CustomTile active={chartPalette === CUSTOM_CHART_PALETTE} color={chartCustomColor} onSelect={() => chooseChartColor(chartCustomColor)} onColor={chooseChartColor} label="Custom data">
              <ChartPalettePreview colors={isDarkMode ? customChartColors.dark : customChartColors.light} dark={isDarkMode} />
            </CustomTile>
          </div>
        </Section>
      )}
    </div>
  );
};

function isDarkColorSafe(hex) {
  return !isLightColor(hex);
}

export default AppearanceSettings;
